import React, { useCallback, useState } from 'react';
import { LoadingStatusValues } from '@/utils/constants';
import ElasticAdapter from '../Adapters/ElasticAdapter';
import { ElasticComponentContextType } from '../ElasticComponent/ElasticComponentContext';
import { parseKeysToFilters } from './elasticSearch/useElasticSearch.utils';
import { useElasticSearch } from './useElasticSearch';

interface ElasticOnDemandProps {
  pageModels: any;
  isElasticAllowed: boolean;
  facetsList: string[];
  keys: string[];
  size: string;
  sortBy: string;
  fields: string[];
}

const useElasticOnDemand = ({
  pageModels,
  isElasticAllowed,
  facetsList,
  keys,
  size = '3',
  sortBy,
  fields,
}: ElasticOnDemandProps) => {
  const [needsUpdate, setNeedsUpdate] = useState<boolean>(false);
  const [status, setStatus] = React.useState<ElasticComponentContextType['status']>(
    LoadingStatusValues.initial,
  );

  const { elasticsearch: elasticsearchConfig } = pageModels.config || {};
  const { search_key: searchKey, resources_engine: engine } = elasticsearchConfig || {};

  const facetsListWithContentCategory = React.useMemo(
    () => ['tax_content_category', ...facetsList],
    [facetsList],
  );

  const filters = parseKeysToFilters(facetsList, pageModels?.taxonomy, keys);

  const defaultParams = React.useRef({
    q: '',
    page: '1',
    sortBy,
    size,
    content_type: [],
    keywords: '',
    filters,
  });

  const search = React.useMemo(() => {
    const composedFilters = ElasticAdapter.encodeFilters(
      filters,
      facetsListWithContentCategory.filter(
        (item, pos) => facetsListWithContentCategory.indexOf(item) === pos,
      ),
    );

    return {
      q: defaultParams.current.q,
      page: defaultParams.current.page,
      sortBy,
      keywords: defaultParams.current.keywords,
      size,
      filters: composedFilters,
    };
  }, [facetsListWithContentCategory, sortBy, size, filters]);

  const es = useElasticSearch({
    needsUpdate,
    engine,
    searchParams: search,
    facets: facetsList,
    fields,
    searchKey,
    language: pageModels.language,
    setNeedsUpdateState: setNeedsUpdate,
  });

  React.useEffect(
    () => {
      if (!isElasticAllowed) return;
      if (!es.isLoading && status === LoadingStatusValues.initial) return;

      setStatus(() => (es.isLoading ? LoadingStatusValues.loading : LoadingStatusValues.ready));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [es.isLoading],
  );

  const fetchElasticSearchData = useCallback(() => {
    setNeedsUpdate(true);
  }, []);

  const sortOptions: string[] = [];

  return {
    es,
    fetchElasticSearchData,
    status,
    sortOptions,
    search,
  };
};

export default useElasticOnDemand;
